import { useEffect } from "react";
import { Dialog, DialogActions, DialogContent, DialogTitle, Button, Grid2 as Grid } from "@mui/material";
import { FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslate } from "@refinedev/core";

import { Form } from "@shared/components";
import { IntegrationEndpointOption, PlanMapOption } from "@modules/erpIntegrations/types";
import { PlanMapOptionFormValues, planMapOptionSchema } from "@modules/erpIntegrations/validations";

interface PlanMapOptionDialogProps {
  integrationId: number;
  endpoints: IntegrationEndpointOption[];
  option: PlanMapOption | "new" | null;
  isSaving: boolean;
  onClose: () => void;
  onSubmit: (data: PlanMapOptionFormValues, existing: PlanMapOption | null) => void;
}

export function PlanMapOptionDialog({
  integrationId,
  endpoints,
  option,
  isSaving,
  onClose,
  onSubmit,
}: PlanMapOptionDialogProps) {
  const translate = useTranslate();
  const isOpen = option !== null;
  const existing = option && option !== "new" ? option : null;

  const buildDefaults = (): PlanMapOptionFormValues => ({
    integration: integrationId,
    endpoint: existing?.endpoint as number,
    label: existing?.label ?? "",
    field: existing?.field ?? "",
    field_type: existing?.field_type ?? "str",
    is_default: existing?.is_default ?? false,
    active: existing?.active ?? true,
  });

  const methods = useForm<PlanMapOptionFormValues>({
    resolver: zodResolver(planMapOptionSchema()),
    defaultValues: buildDefaults(),
  });

  useEffect(() => {
    if (!isOpen) return;
    methods.reset(buildDefaults());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, existing?.id]);

  return (
    <Dialog open={isOpen} onClose={onClose} maxWidth="sm" fullWidth>
      <FormProvider {...methods}>
        <form
          onSubmit={(event) => {
            // This dialog is rendered inside a MUI Dialog, which portals its
            // content to document.body. React's synthetic events still bubble
            // through the REACT tree (not the DOM tree), so without stopping
            // propagation here the submit event would keep bubbling into the
            // outer EditIntegrationModal's own <Form onSubmit>, triggering the
            // integration's PATCH and closing the whole modal.
            event.stopPropagation();
            methods.handleSubmit((data) => onSubmit(data, existing))(event);
          }}
        >
          <DialogTitle>
            {existing
              ? translate("erp_integrations.plan_map_options.edit_title")
              : translate("erp_integrations.plan_map_options.create_title")}
          </DialogTitle>
          <DialogContent dividers>
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid size={{ xs: 12 }}>
                <Form.Input
                  name="label"
                  label={translate("erp_integrations.plan_map_options.label")}
                  required
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Form.Select
                  name="endpoint"
                  label={translate("erp_integrations.plan_map_options.endpoint")}
                  options={endpoints.map((endpoint) => ({
                    value: endpoint.id,
                    label: `${endpoint.slug} — ${endpoint.label}`,
                  }))}
                  required
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Form.Input
                  name="field"
                  label={translate("erp_integrations.plan_map_options.field")}
                  required
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Form.Select
                  name="field_type"
                  label={translate("erp_integrations.plan_map_options.field_type")}
                  options={[
                    { value: "int", label: translate("erp_integrations.plan_map_options.field_type_int") },
                    { value: "str", label: translate("erp_integrations.plan_map_options.field_type_str") },
                  ]}
                  required
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Form.Switch
                  name="is_default"
                  label={translate("erp_integrations.plan_map_options.is_default")}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Form.Switch
                  name="active"
                  label={translate("erp_integrations.plan_map_options.active")}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button type="button" onClick={onClose} disabled={isSaving}>
              {translate("buttons.cancel")}
            </Button>
            <Button type="submit" variant="contained" disabled={isSaving}>
              {translate("buttons.save")}
            </Button>
          </DialogActions>
        </form>
      </FormProvider>
    </Dialog>
  );
}
